
import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FaqItem {
  question: string;
  answer: string;
}

const FaqItem = ({ question, answer }: FaqItem) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-gray-50 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md">
      <button
        className="w-full text-left p-6 flex justify-between items-center focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h3 className="text-xl font-semibold text-gray-900">{question}</h3>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-edgefi-purple flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-edgefi-purple flex-shrink-0" />
        )}
      </button>
      
      <div
        className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96 pb-6 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <p className="text-gray-600">{answer}</p>
      </div>
    </div>
  );
};

export const FaqSection = () => {
  const faqs: FaqItem[] = [
    {
      question: "How quickly can you respond to IT issues?",
      answer: "Our standard SLAs include 15-minute response times for critical issues, with most clients experiencing much faster actual response times. We use a tiered response system based on issue severity to ensure the most important problems are addressed immediately."
    },
    {
      question: "Do we need to replace our internal IT staff?",
      answer: "Not at all. Many of our clients maintain internal IT resources that work collaboratively with our team. We can supplement your existing staff, handling routine tasks and specialized work while your team focuses on strategic initiatives. We're also happy to fully manage your IT if you don't have internal resources."
    },
    {
      question: "How do you handle the transition to managed services?",
      answer: "We follow a structured onboarding process that typically takes 2-4 weeks, depending on environment complexity. This includes thorough documentation, monitoring setup, and knowledge transfer. We prioritize minimal disruption to your operations and clear communication throughout the transition."
    },
    {
      question: "Can you support our specific applications and systems?",
      answer: "Yes, we support a wide range of technologies and applications. Our team has experience with specialized industry software, custom applications, and complex infrastructure. During our assessment phase, we'll document your entire technology stack and ensure we have the expertise to support all critical components."
    },
    {
      question: "How do you ensure our systems stay secure?",
      answer: "Security is integrated into all aspects of our managed services. This includes regular patching, 24/7 threat monitoring, access control management, multi-factor authentication, endpoint protection, and security awareness training. We also perform regular security assessments to identify and address potential vulnerabilities."
    }
  ];

  return (
    <section className="py-16 bg-white" id="faq">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-edgefi-purple/10 text-edgefi-purple rounded-full text-sm font-medium mb-4">FAQ</span>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about our managed IT services.
            </p>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FaqItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
